<template>
    <Head title="Liste des Utilisateurs" />

    <AppLayout>
        <div x-data="handleSelect">
            <Datatables>
                <template #title>
                    Liste des utilisateurs
                    <span class="font-medium">({{ users.total }})</span>
                </template>
    
                <template #headerButtons>
                    <button @click="showModal" title="Ajouter"
                        class="rounded-md py-1 px-2 bg-white text-sky hover:bg-sky-400 hover:text-white"
                    ><i class="fad fa-plus-circle"></i>
                    </button>
                </template>
    
                <template #tableHeader>
                    <th width="3%">#</th>
                    <th width="11%">Code</th>
                    <th width="11%">Nom & prénom(s)</th>
                    <th width="13%">Adresse mail</th>
                    <th width="13%">Téléphone</th>
                    <th width="13%">Rôle</th>
                    <th width="6%"># Colis</th>
                    <th width="7%"># Articles</th>
                    <th width="3%">&nbsp;</th>
                </template>
    
                <template #tableBody>
                    <tr v-for="(user, index) in users.data" :key="user.id">
                        <td>{{ index + 1 }}</td>
                        <td>{{ user.code }}</td>
                        <td class="flex items-center">
                            <div class="shrink-0 flex items-center justify-center bg-gray-700 rounded-full mr-2">
                                <img class="w-10 h-10 rounded-full border-2 border-white box-content" :src="user.profile_photo_url">
                            </div>
                            {{ user.name }}
                        </td>
                        <td>{{ user.email ? user.email : 'Non renseigné' }}</td>
                        <td>{{ user.full_phone }}</td>
                        <td class="capitalize"><StatusBadge :label="user.role_real_text" :color="user.role_real_color" /></td>
                        <td>{{ user.packages_count }}</td>
                        <td>{{ user.items_count }}</td>
                        <td>
                            <Link class="flex justify-end" title="Détails" :href="route('users.show', user)">
                                <svg class="shrink-0 h-6 w-6" viewBox="0 0 24 24">
                                    <path class="fill-current text-sky" d="M8.07 16H10V8H8.07a8 8 0 110 8z"></path>
                                    <path class="fill-current text-sky-300" d="M15 12L8 6v5H0v2h8v5z"></path>
                                </svg>
                            </Link>
                        </td>
                    </tr>
                </template>
            </Datatables>
        </div>

        <Pagination :links="users.links" />

        <JetDialogModal :show="searchModal" @close="closeSearchModal" max-width="xl">
            <template #title>Recherche un utilisateurs</template>

            <template #content>
                <GlobalFilter @search-launched="closeSearchModal" />
            </template>
        </JetDialogModal>

        <JetDialogModal :show="manageModal" @close="closeModal" max-width="2xl">
            <template #title>Ajouter un nouvel utilisateur</template>

            <template #content>
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabelRequired for="name" value="Nom & prénom(s)" />
                        <JetInput id="name" type="text" v-model="form.name" maxlength="80"
                            placeholder="Entrer le nom et prénom(s)" autocomplete="off"
                        />
                        <JetInputError :message="form.errors.name" />
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabel for="email" value="Adresse mail:" />
                         <JetInput id="email" type="email" v-model="form.email"
                            placeholder="Entrer l'adresse mail"  autocomplete="off"
                        />
                        <JetInputError :message="form.errors.email" />
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabelRequired for="code" value="Numéro de téléphone" />
                        <vue-tel-input required @input="phoneInput"
                            :defaultCountry="form.country_code" :value="form.phone"
                        />
                        <JetInputError :message="form.errors.phone" />
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabelRequired for="role" value="Rôle" />
                        <select v-model="form.role" class="form-input w-full uppercase">
                            <option value="null" disabled selected hidden>Choisissez le rôle</option>
                            <option v-for="(role, index) in roles" :key="index" :value="role['value']">
                                {{ role['description'] }}
                            </option>
                        </select>
                        <JetInputError :message="form.errors.role" />
                    </div>
                </div>
            </template>

            <template #footer>
                <BotiButton @click="closeModal" text="Quitter" color="red">
                    <template #icon><i class="fad fa-times-circle text-xs ml-1"></i></template>
                </BotiButton>

                <BotiButton :loader="form.processing" :disabled="form.processing"
                    @click="store"
                    class="ml-2" :class="{ 'opacity-25': form.processing }"
                    text="Enregistrer"
                >
                    <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                    <template #icon>
                        <i class="fad fa-check-circle text-xs ml-1"></i>
                    </template>
                </BotiButton>
            </template>
        </JetDialogModal>
    </AppLayout>
</template>

<script>
    import { defineComponent } from 'vue'
    import JetLabel from '@/Jetstream/Label'
    import JetInput from '@/Jetstream/Input'
    import BotiButton from '@/Components/Button'
    import AppLayout from '@/Layouts/AppLayout.vue'
    import Datatables from '@/Components/Datatables'
    import Pagination from '@/Components/Pagination'
    import StatusBadge from '@/Components/StatusBadge'
    import JetInputError from '@/Jetstream/InputError'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import GlobalFilter from '@/Components/GlobalFilter'
    import { Head, Link } from '@inertiajs/inertia-vue3'
    import JetLabelRequired from '@/Jetstream/LabelRequired'
    import RebuildPhoneMixins from '@/Mixins/RebuildPhoneMixins'

    export default defineComponent({
        components: {
            Link,
            Head,
            JetLabel,
            JetInput,
            AppLayout,
            Datatables,
            Pagination,
            BotiButton,
            StatusBadge,
            GlobalFilter,
            JetInputError,
            JetDialogModal,
            JetLabelRequired
        },

        mixins: [RebuildPhoneMixins],

        props: {
            users: Object,
            roles: Array
        },

        data() {
            return {
                manageModal: false,
                searchModal: false,
                form: this.$inertia.form({
                    name: null,
                    email: null,
                    phone: null,
                    dial_code: null,
                    country_code: null,
                    role: null,
                }),
            }
        },

        methods: {
            closeModal() {
                this.manageModal = false
                this.form.clearErrors()
                this.form.reset()
            },

            showModal() {
                this.form.clearErrors()
                this.manageModal = true
            },

            showSearchModal () {
                this.searchModal = true
            },

            closeSearchModal() {
                this.searchModal = false
                this.form.clearErrors()
            },

            store() {
                this.form.post(route('users.store'), {
                    preserveScroll: true,
                })
            }
        }
    })
</script>
